import { supabase, m, mStr, mArr } from "./core.js";
import { logWarn } from "../shared/logger.js";
import type { Snapshot, SnapshotVersion, DocumentImport } from "../types/index.js";

// ── Latest Snapshot Queries (unchanged) ───────────────────────────

export async function fetchLatestSnapshot(clientId: string): Promise<Snapshot | null> {
  const { data, error } = await supabase
    .from("snapshots")
    .select("*")
    .eq("client_id", clientId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data;
}

export async function fetchSnapshotHistory(clientId: string): Promise<Snapshot[]> {
  const { data, error } = await supabase
    .from("snapshots")
    .select("*")
    .eq("client_id", clientId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchAllLatestSnapshots(): Promise<Snapshot[]> {
  // Try RPC first (optimized), fallback to manual dedup
  const { data: rpcData, error: rpcError } = await supabase.rpc("get_latest_snapshots");
  if (!rpcError && rpcData) return rpcData;

  logWarn("fetchAllLatestSnapshots", "RPC fallback to manual dedup", { error: rpcError?.message });

  const { data, error } = await supabase
    .from("snapshots")
    .select("*")
    .order("updated_at", { ascending: false });
  if (error) throw error;

  // Manual dedup: keep only latest per client
  const seen = new Set<string>();
  const deduped: Snapshot[] = [];
  for (const s of data || []) {
    if (!seen.has(s.client_id)) {
      seen.add(s.client_id);
      deduped.push(s);
    }
  }
  return deduped;
}

export async function fetchSnapshotByPeriod(
  clientId: string,
  period: string
): Promise<Snapshot | null> {
  const { data, error } = await supabase
    .from("snapshots")
    .select("*")
    .eq("client_id", clientId)
    .eq("period", period)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data;
}

// ── Version History Queries ───────────────────────────────────────

export async function fetchSnapshotVersions(
  clientId: string,
  period?: string
): Promise<SnapshotVersion[]> {
  let query = supabase
    .from("snapshot_versions")
    .select("*")
    .eq("client_id", clientId)
    .order("created_at", { ascending: false });

  if (period) query = query.eq("period", period);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as SnapshotVersion[];
}

export async function fetchDocumentImports(clientId: string): Promise<DocumentImport[]> {
  const { data, error } = await supabase
    .from("document_imports")
    .select("*")
    .eq("client_id", clientId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []) as DocumentImport[];
}

// ── Save Version (always INSERT, never overwrite) ─────────────────

async function saveSnapshotVersion(
  clientId: string,
  period: string,
  payload: Partial<Snapshot>,
  snapshotId: string | null,
  filePath?: string,
  fileName?: string
): Promise<SnapshotVersion> {
  // Get next version number via DB function
  const { data: vNum } = await supabase.rpc("next_snapshot_version", {
    p_client_id: clientId,
    p_period: period,
  });
  const versionNumber = (vNum as number) || 1;

  const versionRow = {
    snapshot_id: snapshotId,
    client_id: clientId,
    period,
    version_number: versionNumber,
    ref_date: payload.ref_date || null,
    patrimonio_total: payload.patrimonio_total || null,
    patrimonio_xp: payload.patrimonio_xp || null,
    patrimonio_btg: payload.patrimonio_btg || null,
    patrimonio_inter: payload.patrimonio_inter || null,
    patrimonio_avenue_usd: payload.patrimonio_avenue_usd || null,
    usd_brl_rate: payload.usd_brl_rate || null,
    ret_month: payload.ret_month || null,
    ret_cdi_month: payload.ret_cdi_month || null,
    pct_cdi_month: payload.pct_cdi_month || null,
    ret_12m: payload.ret_12m || null,
    ret_cdi_12m: payload.ret_cdi_12m || null,
    pct_cdi_12m: payload.pct_cdi_12m || null,
    ret_2025: payload.ret_2025 || null,
    ret_2024: payload.ret_2024 || null,
    ret_inception: payload.ret_inception || null,
    acumulado: payload.acumulado || null,
    ganho_month: payload.ganho_month || null,
    ganho_12m: payload.ganho_12m || null,
    volat_12m: payload.volat_12m || null,
    allocation: payload.allocation || null,
    monthly: payload.monthly || null,
    cumulative: payload.cumulative || null,
    periods: payload.periods || null,
    assets_xp: payload.assets_xp || null,
    assets_btg: payload.assets_btg || null,
    assets_inter: payload.assets_inter || null,
    positions_avenue: payload.positions_avenue || null,
    liquidez: payload.liquidez || null,
    movimentacoes: payload.movimentacoes || null,
    benchmarks: payload.benchmarks || null,
    brinson_jan: payload.brinson_jan || null,
    brinson_12m: payload.brinson_12m || null,
    source: payload.source || "manual",
    notes: payload.notes || null,
    file_path: filePath || null,
    file_name: fileName || null,
  };

  const { data, error } = await supabase
    .from("snapshot_versions")
    .insert(versionRow)
    .select()
    .single();
  if (error) throw error;
  return data as SnapshotVersion;
}

// ── Record Document Import ────────────────────────────────────────

export async function recordDocumentImport(opts: {
  clientId: string;
  snapshotVersionId?: string;
  fileName: string;
  filePath: string;
  fileSize?: number;
  fileType?: string;
  broker?: string;
  period?: string;
}): Promise<DocumentImport> {
  const { data, error } = await supabase
    .from("document_imports")
    .insert({
      client_id: opts.clientId,
      snapshot_version_id: opts.snapshotVersionId || null,
      file_name: opts.fileName,
      file_path: opts.filePath,
      file_size: opts.fileSize || null,
      file_type: opts.fileType || null,
      broker: opts.broker || null,
      period: opts.period || null,
      status: "completed",
    })
    .select()
    .single();
  if (error) throw error;
  return data as DocumentImport;
}

// ── Upsert Latest + Save Version ──────────────────────────────────

export interface UpsertOptions {
  filePath?: string;
  fileName?: string;
}

export async function upsertSnapshot(
  clientId: string,
  period: string,
  payload: Partial<Snapshot>,
  opts?: UpsertOptions
): Promise<Snapshot> {
  const existing = await fetchSnapshotByPeriod(clientId, period);

  const merged = existing
    ? {
        ...existing,
        patrimonio_total: m(payload.patrimonio_total, existing.patrimonio_total),
        period: mStr(payload.period, existing.period),
        ref_date: mStr(payload.ref_date, existing.ref_date),
        updated_at: new Date().toISOString(),
        assets_xp: mArr(payload.assets_xp as unknown[], existing.assets_xp as unknown[]),
        assets_btg: mArr(payload.assets_btg as unknown[], existing.assets_btg as unknown[]),
        assets_inter: mArr(payload.assets_inter as unknown[], existing.assets_inter as unknown[]),
        positions_avenue: mArr(
          payload.positions_avenue as unknown[],
          existing.positions_avenue as unknown[]
        ),
        allocation: mArr(payload.allocation as unknown[], existing.allocation as unknown[]),
      }
    : { ...payload, client_id: clientId, period };

  // 1. Upsert latest snapshot (keeps backward compatibility)
  const { data, error } = await supabase
    .from("snapshots")
    .upsert(merged, { onConflict: "client_id,period" })
    .select()
    .single();
  if (error) throw error;

  // 2. Save version history (never overwrites — always INSERT)
  try {
    await saveSnapshotVersion(
      clientId,
      period,
      { ...payload, source: payload.source },
      data.id,
      opts?.filePath,
      opts?.fileName
    );
  } catch (vErr) {
    // Non-blocking: log but don't fail the main operation
    logWarn("upsertSnapshot", "Failed to save version history", {
      error: (vErr as Error).message,
      clientId,
      period,
    });
  }

  return data;
}

// ── Ingest Extrato (with file tracking) ───────────────────────────

export interface IngestOptions {
  filePath?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  broker?: string;
}

export async function ingestExtrato(
  clientId: string,
  period: string,
  payload: Partial<Snapshot>,
  opts?: IngestOptions
): Promise<Snapshot> {
  const snapshot = await upsertSnapshot(
    clientId,
    period,
    {
      ...payload,
      updated_at: new Date().toISOString(),
      source: "pdf_generico",
    },
    { filePath: opts?.filePath, fileName: opts?.fileName }
  );

  // 3. Record file import in document_imports table
  if (opts?.filePath) {
    try {
      await recordDocumentImport({
        clientId,
        fileName: opts.fileName || "unknown",
        filePath: opts.filePath,
        fileSize: opts.fileSize,
        fileType: opts.fileType,
        broker: opts.broker,
        period,
      });
    } catch (diErr) {
      logWarn("ingestExtrato", "Failed to record document import", {
        error: (diErr as Error).message,
        clientId,
        period,
      });
    }
  }

  return snapshot;
}
